// Thermal Print Service (TPS) types — TPS 2.x API (http://127.0.0.1:38445).
// Spec: dhaba-print-middleware docs/superpowers/specs/2026-09-28-tps-production-readiness-design.md §5.

/** Live printer status. `null` until the service has checked the printer once. */
export interface TPSPrinterStatus {
  online: boolean;
  paper_out: boolean;
  cover_open: boolean;
  error: string | null;
}

export type TPSPrinterType = "windows" | "network" | "usb" | "serial" | "bluetooth" | "cups";

export interface TPSPrinter {
  id: string;
  name: string;
  type: TPSPrinterType | string;
  is_default: boolean;
  auto_discovered: boolean;
  status: TPSPrinterStatus | null;
  /** Not sent by TPS 2.x; kept optional for display only. */
  port_name?: string;
}

/** The service has checked the printer and it answered. */
export function isPrinterOnline(printer: TPSPrinter): boolean {
  return printer.status?.online === true;
}

/**
 * The service checked the printer and it did not answer. A printer that has not
 * been checked yet (`status: null`) is not offline: it can still be chosen and printed to.
 */
export function isPrinterOffline(printer: TPSPrinter): boolean {
  return printer.status != null && !printer.status.online;
}

/** Get a display label for printer status */
export function printerStatusLabel(printer: TPSPrinter): string {
  if (!printer.status) return "Checking";
  if (!printer.status.online) return "Offline";
  if (printer.status.paper_out) return "Paper Out";
  if (printer.status.cover_open) return "Cover Open";
  if (printer.status.error) return "Error";
  return "Online";
}

/** Every response body is `{success, data}` on success and `{success:false, error, code}` on failure. */
export interface TPSEnvelope<T> {
  success: boolean;
  data: T;
}

export interface TPSErrorBody {
  success: false;
  error: string;
  code?: TPSErrorCode | string;
}

export type TPSErrorCode =
  | "PAIRING_REQUIRED"
  | "PAIRING_DENIED"
  | "PAIRING_TIMEOUT"
  | "PAIRING_IN_PROGRESS"
  | "RATE_LIMITED"
  | "INVALID_REQUEST"
  | "PRINTER_NOT_FOUND"
  | "PAYLOAD_TOO_LARGE"
  | "UNSUPPORTED_MEDIA_TYPE"
  | "ESCPOS_FORBIDDEN_COMMAND"
  | "JOB_NOT_FOUND"
  | "JOB_NOT_CANCELLABLE"
  | "HOST_NOT_ALLOWED"
  | "ORIGIN_REQUIRED"
  | "ORIGIN_NOT_ALLOWED"
  | "SHUTTING_DOWN"
  | "INTERNAL";

export interface TPSPing {
  pong: boolean;
  service: string;
  version: string;
  api_version: number;
}

export interface TPSPairResult {
  token: string;
  origin: string;
}

export type TPSPrintersResponse = TPSEnvelope<{ printers: TPSPrinter[] }>;

export interface TPSPrintRequest {
  printer_id?: string;
  printer_ids?: string[];
  data: string; // Base64-encoded ESC/POS bytes
  copies?: number;
  job_meta?: Record<string, unknown> & { open_cash_drawer?: boolean };
}

/** POST /print answers 202: one `job_id`, or `job_ids` when `printer_ids` has several. */
export type TPSPrintResponse = TPSEnvelope<{
  job_id?: string;
  job_ids?: string[];
  status: TPSJobStatus;
}>;

export type TPSJobStatus =
  | "queued"
  | "printing"
  | "retrying"
  | "completed"
  | "failed"
  | "failed_unknown"
  | "expired"
  | "cancelled";

/** No further change will happen to a job in one of these states. */
export const TPS_FINAL_JOB_STATUSES: readonly TPSJobStatus[] = [
  "completed",
  "failed",
  "failed_unknown",
  "expired",
  "cancelled",
];

export function isFinalJobStatus(status: string): status is TPSJobStatus {
  return (TPS_FINAL_JOB_STATUSES as readonly string[]).includes(status);
}

export interface TPSJob {
  job_id: string;
  printer_id: string;
  status: TPSJobStatus;
  copies: number;
  attempts: number;
  created_at: string;
  updated_at: string;
  last_error: string | null;
  completed_at: string | null;
  job_meta: Record<string, unknown> | null;
}

export type TPSJobStatusResponse = TPSEnvelope<TPSJob>;

/** Persisted printer assignments for this machine + outlet */
export interface PrinterAssignments {
  bill_printer_id: string | null;
  kot_printer_id: string | null;
}

export const PRINTER_ASSIGNMENTS_KEY_PREFIX = "dhaba_printer_assignments_";
export const DEMO_PRINTING_KEY_PREFIX = "dhaba_demo_printing_enabled_";
/** localStorage key prefix for the pairing token; the TPS base URL is appended. */
export const TPS_TOKEN_KEY_PREFIX = "dhaba_tps_token:";
